import React, { useEffect, useState } from "react";
import "./UpsellNotification.scss";
import { useNavigate } from "react-router-dom";
import { UserUpsellNotificationDetail } from "../../../types/upsell-notification/UserUpsellNotificationDetail";
import useAuth from "../../../hooks/useAuth";
import upsellNotificationService from "../../../services/upsellNotification.service";
import { formatDateTime } from "../../../utils/dateTimeUtils";

const SLIDE_DURATION = 280;

interface Props {
    notifications: UserUpsellNotificationDetail[];
    onClose?: () => void;
    onSuppress?: (historyId: number) => void;
}

// ============= Notification Type Formatters =============
// Each formatter handles a specific upsellNotificationTypeId
interface NotificationData {
    oldData: unknown;
    newData: unknown;
}

interface FormattedNotification {
    mainText: string;
    upgradeText?: string;
}

type NotificationFormatter = (data: NotificationData) => FormattedNotification;

const UPGRADE_MESSAGE = "Upgrade to Ultimate CIQ license to know";

// Helper function to format date strings nicely
// const formatDate = (dateString: string): string => {
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
// };

// Formatter for Sentiment (Type 2)
const formatSentimentNotification: NotificationFormatter = (data) => {
    const oldData = data.oldData as Record<string, number>;
    const newData = data.newData as Record<string, number>;
    
    return {
        mainText: `Sentiment changed - Positive: ${oldData.positive}→${newData.positive}, Negative: ${oldData.negative}→${newData.negative}, Neutral: ${oldData.neutral}→${newData.neutral}`,
        upgradeText: UPGRADE_MESSAGE,
    };
};

// Formatter for CallTime (Type 1)
const formatCallTimeNotification: NotificationFormatter = (data) => {
    const oldTime = data.oldData as string;
    const newTime = data.newData as string;
    
    return {
        mainText: `Call time changed from ${oldTime} to ${newTime}`,
        upgradeText: UPGRADE_MESSAGE,
    };
};

// Formatter not found - fallback
const formatDefaultNotification: NotificationFormatter = (data) => {
    const { oldData, newData } = data;
    return {
        mainText: `Old: ${JSON.stringify(oldData)}, New: ${JSON.stringify(newData)}`,
        upgradeText: UPGRADE_MESSAGE,
    };
};

// Map notificationTypeId to formatter function
const notificationFormatters: Record<number, NotificationFormatter> = {
    1: formatCallTimeNotification,
    2: formatSentimentNotification,
};

const getNotificationFormatter = (typeId: number | undefined): NotificationFormatter => {
    return notificationFormatters[typeId ?? 0] || formatDefaultNotification;
};

const buildSlides = (
    notifications: UserUpsellNotificationDetail[]
): { id: string; heading: string; mainText: string; upgradeText?: string; weekStartDate?: string; weekEndDate?: string }[] => {
    return notifications.map((n) => {
        let heading = n.notificationType || "";
        let mainText = "";
        let upgradeText: string | undefined = undefined;
        let weekStartDate: string | undefined;
        let weekEndDate: string | undefined;

        try {
            const decoded = atob(n.notificationObject || "");
            const parsed = JSON.parse(decoded);
            const data = parsed?.data;
            weekStartDate = parsed?.weekStartDate;
            weekEndDate = parsed?.weekEndDate;
            if (data) {
                // Get the appropriate formatter based on upsellNotificationTypeId
                const formatter = getNotificationFormatter(n.upsellNotificationTypeId);
                const formatted = formatter(data);
                mainText = formatted.mainText;
                upgradeText = formatted.upgradeText;
            }
        } catch {
            // raw string if parsing fails
            mainText = n.notificationObject || "";
        }

        return {
            id: n.userUpsellNotificationHistoryId.toString(),
            heading,
            mainText,
            upgradeText,
            weekStartDate,
            weekEndDate,
        };
    });
};

const UpsellNotification: React.FC<Props> = ({ notifications, onClose, onSuppress }) => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(true);
    const [isClosing, setIsClosing] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const slides = buildSlides(notifications);

    useEffect(() => {
        setActiveIndex((prev) => {
            if (slides.length === 0) return 0;
            return Math.min(prev, slides.length - 1);
        });
    }, [slides.length]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && isOpen) dismissPopover();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen]);

    const auth = useAuth();

    const dismissPopover = () => {
        if (isClosing) return;
        setIsClosing(true);
        setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
            onClose && onClose();
        }, SLIDE_DURATION);
    };

    const handleSuppress = async () => {
        const displayIndex = slides.length > 0 ? Math.min(activeIndex, slides.length - 1) : 0;
        const current = notifications[displayIndex];
        if (auth?.tokenPayload?.UserId && current) {
            try {
                await upsellNotificationService.updateNotificationHistory(
                    auth.tokenPayload.UserId,
                    current.userUpsellNotificationHistoryId,
                    { isUpsellNotificationSuppressed: true, markAsShown: true }
                );
            } catch (err) {
                console.error("Error updating upsell notification history", err);
            }
        }
        if (current) {
            onSuppress && onSuppress(current.userUpsellNotificationHistoryId);
        }
    };

    const handleUpgrade = () => navigate("/upgrade-licenses");

    const goTo = (index: number) => setActiveIndex(index);

    if (slides.length === 0) return null;

    // pick a stable index for rendering; avoids out-of-bounds when slides shrink
    const displayIndex = Math.min(activeIndex, slides.length - 1);

    const goPrev = () => setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
    const goNext = () => setActiveIndex((prev) => (prev + 1) % slides.length);

    if (!isOpen) return null;

    return (
        <div
            className={`upsell-popover ${isClosing ? "closing" : "open"}`}
            role="dialog"
        >
            <button
                aria-label="Close popover"
                className="upsell-close"
                onClick={dismissPopover}
            >
                ×
            </button>

            <div className="upsell-body">
                <h4 className="text-center">{slides[displayIndex].heading}</h4>
                <p className="text-muted text-center">{slides[displayIndex].mainText}</p>
                {slides[displayIndex].upgradeText && (
                    <p className="text-muted text-center" style={{ fontSize: "0.875rem", marginTop: "0.75rem" }}>
                        <em>{slides[displayIndex].upgradeText}</em>
                    </p>
                )}
                {slides[displayIndex].weekStartDate && slides[displayIndex].weekEndDate && (
                    <p className="text-muted text-center" style={{ fontSize: "0.875rem", marginTop: "0.5rem" }}>
                        Week: {formatDateTime(slides[displayIndex].weekStartDate!)} to {formatDateTime(slides[displayIndex].weekEndDate!)}
                    </p>
                )}
            </div>

            <div className="upsell-actions">
                <button className="btn btn-upsell btn-ghost" onClick={handleSuppress}>
                    Suppress this
                </button>
                <button className="btn btn-upsell btn-primary" onClick={handleUpgrade}>
                    Upgrade
                </button>
            </div>

            {slides.length > 1 && (
                <div className="upsell-carousel">
                    <button
                        className="nav-btn"
                        onClick={goPrev}
                    >
                        <i className="bi bi-chevron-left" />
                    </button>

                    <div className="dot-group" role="tablist">
                        {slides.map((slide, idx) => (
                            <button
                                key={slide.id ?? idx}
                                className={`dot ${idx === displayIndex ? "active" : ""}`}
                                onClick={() => goTo(idx)}
                            >
                                <i className="bi bi-circle-fill" />
                            </button>
                        ))}
                    </div>

                    <button
                        className="nav-btn"
                        onClick={goNext}
                    >
                        <i className="bi bi-chevron-right" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default UpsellNotification;
